import { forwardRef } from "react";
import { View, type ViewProps } from "@src/components/View";
import { useUnistyles } from "react-native-unistyles";

export interface CardProps extends Omit<ViewProps, "px" | "py" | "paddingX" | "paddingY"> {
  /**
   * Override horizontal padding (defaults to sizes.sm = 16px)
   * Can be a spacing token (e.g., "xs", "sm") or a number in pixels
   */
  px?: ViewProps["px"] | number;
  /**
   * Override vertical padding (defaults to sizes.sm = 16px)
   * Can be a spacing token (e.g., "xs", "sm") or a number in pixels
   */
  py?: ViewProps["py"] | number;
  /**
   * Override border radius (defaults to 8)
   */
  rounded?: ViewProps["rounded"];
}

/**
 * Card component with standardized padding and styling.
 *
 * Default padding: 16px horizontal, 16px vertical (uses sizes.sm)
 * Default border radius: 8px
 */
export const Card = forwardRef<React.ComponentRef<typeof View>, CardProps>(
  ({ px, py, rounded = 12, bg = "surface", borderWidth = 1, borderColor = "border", style, ...props }, ref) => {
    const { theme } = useUnistyles();
    // If px/py are provided as spacing tokens, use View's built-in support
    // Otherwise, use pixel values from spacing.ts
    const paddingHorizontal = typeof px === "string" ? undefined : (px ?? theme.spacing["4xl"]);
    const paddingVertical = typeof py === "string" ? undefined : (py ?? theme.spacing["4xl"]);

    return (
      <View
        ref={ref}
        bg={bg}
        borderWidth={borderWidth}
        borderColor={borderColor}
        rounded={rounded}
        borderCurve="continuous"
        px={typeof px === "string" ? px : undefined}
        py={typeof py === "string" ? py : undefined}
        style={[
          paddingHorizontal !== undefined && { paddingHorizontal },
          paddingVertical !== undefined && { paddingVertical },
          style,
        ].filter(Boolean)}
        {...props}
      />
    );
  },
);

Card.displayName = "Card";
